import { useEffect, useMemo, useCallback } from 'react';
import type { Highlight, HighlightMoveHandler } from '../types';
import { toNormalized } from '../helpers/coords';

import { useAnnotationMode } from './useAnnotationMode';
import type { AnnotationMode } from './useAnnotationMode';
import { useFreehandDrawing } from './useFreehandDrawing';
import { useRectDrawing } from './useRectDrawing';
import { usePolygonDrawing } from './usePolygonDrawing';
import { useMoveDrag } from './useMoveDrag';

interface UseViewerModesOptions {
    pdfLayersRef: React.RefObject<HTMLDivElement | null>;
    overlayRef: React.RefObject<HTMLDivElement | null>;
    dimension: { width: number; height: number };
    pageNumber: number;
    pdfDocIdRef: React.MutableRefObject<string>;
    onSaveRef?: React.MutableRefObject<((highlights: Highlight[]) => void) | undefined>;
    onHighlightMoveRef?: React.MutableRefObject<HighlightMoveHandler | undefined>;
    highlightsRef?: React.MutableRefObject<Highlight[]>;
    setHighlights: React.Dispatch<React.SetStateAction<Highlight[]>>;
    setActivePopup: React.Dispatch<React.SetStateAction<any>>;
    setEditingHighlight: React.Dispatch<React.SetStateAction<{ id: string; note: string } | null>>;
    setPendingPolygon: React.Dispatch<React.SetStateAction<Array<{ x: number; y: number }> | null>>;
    setPendingComment: React.Dispatch<React.SetStateAction<string>>;
    setPendingRect: React.Dispatch<React.SetStateAction<{ x1: number; y1: number; x2: number; y2: number } | null>>;
    setPendingMarker: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
}

export function useViewerModes({
    pdfLayersRef,
    overlayRef,
    dimension,
    pageNumber,
    pdfDocIdRef,
    onSaveRef,
    onHighlightMoveRef,
    highlightsRef,
    setHighlights,
    setActivePopup,
    setEditingHighlight,
    setPendingPolygon,
    setPendingComment,
    setPendingRect,
    setPendingMarker,
}: UseViewerModesOptions) {

    const polygon = usePolygonDrawing(dimension, overlayRef, setPendingPolygon, setPendingComment);
    const freehand = useFreehandDrawing(pdfLayersRef, setPendingComment, setPendingPolygon);
    const rect = useRectDrawing(pdfLayersRef, dimension, setPendingRect, setPendingComment);

    const moveDrag = useMoveDrag(
        null as any,
        pdfLayersRef,
        dimension,
        pdfDocIdRef,
        onSaveRef,
        setHighlights,
        setActivePopup,
        onHighlightMoveRef,
        highlightsRef,
    );

    const modeHandlers = useMemo((): Record<string, (e: React.MouseEvent) => void> => ({
        polygon: polygon.handleContainerClick,
        freehand: (e: React.MouseEvent) => freehand.startFreehand(e),
        placing: (e: React.MouseEvent) => {
            const overlay = overlayRef.current;
            if (!overlay || !dimension.width || !dimension.height) return;
            const base = overlay.getBoundingClientRect();
            const { nx, ny } = toNormalized(e.clientX, e.clientY, base, dimension.width, dimension.height);
            setPendingMarker({ x: nx, y: ny });
            setPendingComment('');
        },
        hand: () => {},
        move: () => {},
        text: () => {},
        rect: () => {},
    }), [polygon.handleContainerClick, freehand.startFreehand, overlayRef, dimension, setPendingMarker, setPendingComment]);

    const doubleClickHandler = useCallback((e: React.MouseEvent, activeMode: AnnotationMode | null) => {
        if (activeMode === 'polygon') {
            polygon.handleContainerDoubleClick(e);
        }
    }, [polygon.handleContainerDoubleClick]);

    const controller = useAnnotationMode(modeHandlers, doubleClickHandler);
    const { activeMode, activateMode, deactivateMode, handleContainerClick, handleContainerDoubleClick } = controller;

    useEffect(() => {
        if (activeMode !== 'move') {
            moveDrag.moveDragRef.current = null;
            document.body.style.cursor = '';
        }
    }, [activeMode, moveDrag.moveDragRef]);

    useEffect(() => {
        if (activeMode !== 'freehand' && freehand.freehandDrawingRef.current) {
            freehand.resetFreehand();
        }
    }, [activeMode, freehand]);

    useEffect(() => {
        if (activeMode !== 'polygon') {
            polygon.setDraftPoints([]);
        }
    }, [activeMode, polygon.setDraftPoints]);

    useEffect(() => {
        if (activeMode !== 'rect') {
            rect.resetRect();
        }
    }, [activeMode, rect]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setActivePopup(null);
            moveDrag.moveDragRef.current = null;
            if (freehand.freehandDrawingRef.current) freehand.resetFreehand();
            if (rect.rectDragging) rect.resetRect();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [freehand, rect, moveDrag, setActivePopup]);

    return {
        placing: activeMode === 'placing',
        setPlacing: () => activateMode('placing'),
        textMode: activeMode === 'text',
        setTextMode: () => activateMode('text'),
        handMode: activeMode === 'hand',
        setHandMode: () => activateMode('hand'),
        polygonMode: activeMode === 'polygon',
        setPolygonMode: () => activateMode('polygon'),
        draftPoints: polygon.draftPoints,
        setDraftPoints: polygon.setDraftPoints,
        rectMode: activeMode === 'rect',
        setRectMode: () => activateMode('rect'),
        rectDragging: rect.rectDragging,
        setRectDragging: rect.setRectDragging,
        rectDraft: rect.rectDraft,
        setRectDraft: rect.setRectDraft,
        freehandMode: activeMode === 'freehand',
        setFreehandMode: () => activateMode('freehand'),
        freehandActive: freehand.freehandActive,
        moveMode: activeMode === 'move',
        setMoveMode: () => activateMode('move'),
        freehandPolylineRef: freehand.freehandPolylineRef,
        freehandStartCircleRef: freehand.freehandStartCircleRef,
        moveDragRef: moveDrag.moveDragRef,
        resetFreehand: freehand.resetFreehand,
        handleContainerClick,
        handleContainerDoubleClick,
        activeMode,
        activateMode,
        deactivateMode,
    };
}
